import {DatasourceParams, PageableDatasource, PaginatedData} from '../pageable-datasource';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {first, ReplaySubject, takeUntil} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {Destroyable} from '../../components/destroyable/destroyable.component';
import {PAGE_SIZE_5} from '../../services/model';

/**
 * By default, the pageable component handles the initialization and event of mat paginator and only
 * defining <mat-paginator> in the template is sufficient.
 *
 * With the input flag: customPagingHandling paginator handling can be implemented in the child components.
 *
 * Example handing in he html:
 * <mat-paginator *ngIf="dataSource.page$ | async as page" [length]="page.page.totalElements" [pageSize]="page.page.size"
 *                    [pageIndex]="page.page.number" [hidePageSize]="true" (page)="onPageChange($event.pageIndex)">
 *     </mat-paginator>
 *
 * MatSort have strange behaviour when updated programmatically:
 * https://stackoverflow.com/questions/54809837/set-sorting-of-matsort-in-code-after-proxy-call-results-in-expressionchangedafte
 *
 * If needed template should be used like:
 * <table mat-table [dataSource]="dataSource" matSort (matSortChange)="onSortData($event)" [matSortActive]="sort?.property!"
 *            [matSortDirection]="sort?.direction!">
 */
/**
 * By default, the pageable component handled the initialization and event of mat paginator if defined and only
 * defining <mat-paginator> in the template is sufficient.
 *
 * With the input flag: customPagingHandling paginator handling can be implemented in the child components.
 *
 * Example handing in he html:
 * <mat-paginator *ngIf="dataSource.page$ | async as page" [length]="page.page.totalElements" [pageSize]="page.page.size"
 *                    [pageIndex]="page.page.number" [hidePageSize]="true" (page)="onPageChange($event.pageIndex)">
 *     </mat-paginator>
 *
 * MatSort have strange behaviour when updated programmatically:
 * https://stackoverflow.com/questions/54809837/set-sorting-of-matsort-in-code-after-proxy-call-results-in-expressionchangedafte
 *
 * If needed template should be used like:
 * <table mat-table [dataSource]="dataSource" matSort (matSortChange)="onSortData($event)" [matSortActive]="sort?.property!"
 *            [matSortDirection]="sort?.direction!">
 */
export abstract class PageableComponent<T, Q> extends Destroyable {

  protected dataSource: PageableDatasource<T, Q>;
  private readonly pageParameters = new ReplaySubject<DatasourceParams<T, Q>>(1);
  protected readonly pageParameters$ = this.pageParameters.asObservable();

  protected constructor(private customPagingHandling = false) {
    super();
    this.pageParameters.next({page: 0, filter: this.getFilter()});
    this.dataSource = new PageableDatasource<T, Q>(this.loadPaginatedData(), this.getPageSize(), this.pageParameters$.pipe(takeUntil(this.destroy$)));
  }

  abstract loadPaginatedData(): PaginatedData<T, Q>;

  onPageChange(pageIndex: number): void {
    this.pageParameters.next({page: pageIndex});
  }

  protected getFilter(): Q | undefined {
    return undefined;
  }

  protected getPageSize(): number {
    return PAGE_SIZE_5;
  }

  protected initPaginator(paginator: MatPaginator): void {
    if (paginator && !this.customPagingHandling) {
      paginator.page.pipe(takeUntil(this.destroy$),
        distinctUntilChanged())
        .subscribe((event: PageEvent) => {
          this.onPageChange(event.pageIndex);
        });
    }
  }

  protected reload(): void {
    this.pageParameters$.pipe(first())
      .subscribe(params => {
        this.pageParameters.next(params);
      });
  }

}

